import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { COUNTRIES } from "../../lib/constants";
import { toast } from "sonner";

/**
 * GEO-2B: Focused quick-correction modal for a single lead's city/country.
 *
 * Only city and country are editable. Other fields stay untouched.
 *
 * Props:
 *  - open:    boolean
 *  - lead:    { id, company_name, city, country } | null
 *  - onOpenChange(boolean)
 *  - onSaved(updatedLead): called after a successful PUT /api/leads/{id}.
 */
export default function LocationFixModal({ open, lead, onOpenChange, onSaved }) {
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setCity(lead.city || "");
      setCountry(lead.country || "");
    }
  }, [lead]);

  const submit = async () => {
    if (!lead) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/leads/${lead.id}`, {
        city: city.trim(),
        country: country.trim(),
      });
      toast.success("Location updated");
      onOpenChange(false);
      if (typeof onSaved === "function") onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="location-fix-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Fix location</DialogTitle>
          <DialogDescription className="text-xs">
            {lead?.company_name
              ? `Update city/country for ${lead.company_name}. Other fields are not changed.`
              : "Update city/country."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="locfix-city">City</Label>
            <Input
              id="locfix-city"
              data-testid="locfix-city-input"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Milan"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label>Country</Label>
            <Select value={country || ""} onValueChange={setCountry}>
              <SelectTrigger data-testid="locfix-country-select">
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="locfix-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !city.trim() || !country}
            data-testid="locfix-save"
            className="bg-[#1A3A21] hover:bg-[#142C19] text-white"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
